import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { FileText, Download, Trash2, Eye, Search, Filter, Calendar, X } from 'lucide-react';
import { toast } from 'sonner';
import { Document, Page, pdfjs } from 'react-pdf';

// PDF.js Worker konfigurieren - Use CDN mit kompatibler Version
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const Dokumente = () => {
  const [dokumente, setDokumente] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTyp, setFilterTyp] = useState('alle');
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);

  useEffect(() => {
    loadDokumente();
  }, []);

  const loadDokumente = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('autogutachter24_token');
      
      // Alle PDFs von allen Akten laden
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/list-all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Dokumente');
      }

      const data = await response.json();
      setDokumente(data.gutachten || []);
    } catch (error) {
      console.error('Error loading dokumente:', error);
      toast.error('Fehler beim Laden der Dokumente');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (gutachtenId) => {
    if (!window.confirm('Möchten Sie dieses Dokument wirklich löschen?')) {
      return;
    }

    try {
      const token = localStorage.getItem('autogutachter24_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/${gutachtenId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Löschen');
      }

      toast.success('Dokument gelöscht');
      loadDokumente();
      
      // Schließe Vorschau wenn aktuell geöffnet
      if (selectedPDF?.gutachten_id === gutachtenId) {
        setSelectedPDF(null);
        setPdfPreviewUrl(null);
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Fehler beim Löschen des Dokuments');
    }
  };

  const handleDownload = async (gutachtenId, filename) => {
    try {
      const token = localStorage.getItem('autogutachter24_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/download/${gutachtenId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Download');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Download gestartet');
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Fehler beim Download');
    }
  };

  const handlePreview = async (dokument) => {
    try {
      setSelectedPDF(dokument);
      setPageNumber(1);
      
      const token = localStorage.getItem('autogutachter24_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/download/${dokument.gutachten_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Vorschau');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setPdfPreviewUrl(url);
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Fehler beim Laden der Vorschau');
    }
  };

  const closePreview = () => {
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
    }
    setSelectedPDF(null);
    setPdfPreviewUrl(null);
    setNumPages(null);
    setPageNumber(1);
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // Filter und Suche
  const filteredDokumente = dokumente.filter(dok => {
    const matchesSearch = 
      dok.aktenzeichen?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dok.pdf_filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dok.typ?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterTyp === 'alle' || dok.typ === filterTyp;
    
    return matchesSearch && matchesFilter;
  });

  // Einzigartige Typen für Filter
  const uniqueTypes = [...new Set(dokumente.map(d => d.typ))];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Dokumente</h1>
          <p className="text-gray-600">Verwalten Sie alle generierten PDF-Dokumente</p>
        </div>

        {/* Suchleiste und Filter */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Suche */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Suche nach Aktenzeichen, Dateiname oder Typ..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Filter */}
              <div className="flex items-center gap-2">
                <Filter className="text-gray-400 w-5 h-5" />
                <select
                  value={filterTyp}
                  onChange={(e) => setFilterTyp(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="alle">Alle Typen</option>
                  {uniqueTypes.map(typ => (
                    <option key={typ} value={typ}>{typ}</option>
                  ))}
                </select>
              </div>

              {/* Aktualisieren Button */}
              <Button onClick={loadDokumente} variant="outline">
                Aktualisieren
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Dokumente Grid */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredDokumente.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Keine Dokumente gefunden</h3>
              <p className="text-gray-600">
                {searchTerm || filterTyp !== 'alle' 
                  ? 'Versuchen Sie eine andere Suche oder Filter.'
                  : 'Erstellen Sie Ihr erstes PDF-Dokument.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDokumente.map((dok) => (
              <Card key={dok.gutachten_id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <FileText className="w-8 h-8 text-blue-600" />
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {dok.typ}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <h3 className="font-semibold text-gray-900 mb-2 truncate">
                    {dok.aktenzeichen || 'Unbekannt'}
                  </h3>
                  <p className="text-sm text-gray-600 mb-1 truncate">
                    {dok.pdf_filename}
                  </p>
                  <div className="flex items-center text-xs text-gray-500 mb-4">
                    <Calendar className="w-3 h-3 mr-1" />
                    {new Date(dok.created_at).toLocaleDateString('de-DE')}
                  </div>

                  {/* Aktionen */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => handlePreview(dok)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Vorschau
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(dok.gutachten_id, dok.pdf_filename)}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(dok.gutachten_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* PDF Vorschau Modal */}
        {selectedPDF && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg w-full max-w-4xl h-[90vh] flex flex-col">
              {/* Modal Header */}
              <div className="p-4 border-b flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedPDF.aktenzeichen}</h3>
                  <p className="text-sm text-gray-600">{selectedPDF.pdf_filename}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={closePreview}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* PDF Viewer */}
              <div className="flex-1 overflow-auto bg-gray-100 p-4">
                {pdfPreviewUrl ? (
                  <div className="flex flex-col items-center">
                    <Document
                      file={pdfPreviewUrl}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={(error) => {
                        console.error('PDF Load Error:', error);
                        toast.error('Fehler beim Laden des PDFs');
                      }}
                      loading={
                        <div className="flex justify-center items-center h-96">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        </div>
                      }
                    >
                      <Page
                        pageNumber={pageNumber}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                        width={Math.min(window.innerWidth - 100, 800)}
                      />
                    </Document>
                  </div>
                ) : (
                  <div className="flex justify-center items-center h-96">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  </div>
                )}
              </div>

              {/* Navigation */}
              {numPages && (
                <div className="p-4 border-t flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pageNumber <= 1}
                    onClick={() => setPageNumber(page => page - 1)}
                  >
                    Vorherige
                  </Button>
                  <span className="text-sm text-gray-600">
                    Seite {pageNumber} von {numPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pageNumber >= numPages}
                    onClick={() => setPageNumber(page => page + 1)}
                  >
                    Nächste
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dokumente;
